import datacentral from '@/utils/axios/datacentral'
import { Tender, TenderBidResult, TenderChapter } from '../types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'

export const ListTenders = async (
    payload: DataGridPayload
): Promise<ResponseArray<Tender>> => {
    const { page, page_size, q, ...rest } = payload
    const response = await datacentral.get('/bot-tenders', {
        params: {
            page,
            pageSize: page_size,
            search: q,
            ...rest,
        },
    })
    return response.data
}

export const ListFollowingTenders = async (
    payload: DataGridPayload,
    employee_id: number
): Promise<ResponseArray<Tender>> => {
    const { page, page_size, q, ...rest } = payload
    const response = await datacentral.get('/bot-tenders/followed', {
        params: {
            page,
            pageSize: page_size,
            search: q,
            employee_id,
            ...rest,
        },
    })
    return response.data
}

export const FollowTender = async (tender_id: number, employee_id: number) => {
    const response = await datacentral.post(
        `/bot-tenders/${tender_id}/followers`,
        {
            follower_id: employee_id,
        }
    )
    return response.data
}

export const ListUnfollowedTenders = async (
    payload: DataGridPayload
): Promise<ResponseArray<Tender>> => {
    const { page, page_size, q, ...rest } = payload
    const response = await datacentral.get('/bot-tenders/unfollowed', {
        params: {
            page,
            pageSize: page_size,
            search: q,
            ...rest,
        },
    })
    return response.data
}

export const UnfollowTender = async (
    tender_id: number,
    employee_id: number
) => {
    const response = await datacentral.delete(
        `/bot-tenders/${tender_id}/followers/${employee_id}`
    )
    return response.data
}

// Parse filename from content-disposition header
export const extractFilenameFromHeader = (
    contentDisposition?: string
): string => {
    if (!contentDisposition) return 'download'
    // e.g. attachment; filename=2025101017059247_Ch%C6%B0%C6%A1ng+V.pdf
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^\";]+)"?/i.exec(
        contentDisposition
    )
    const raw = decodeURIComponent(match?.[1] || match?.[2] || 'download')
    // Replace plus with space if server sent classic url-encoding
    return raw.replace(/\+/g, ' ')
}

export const GetTenderDocument = async (file_id: string) => {
    const response = await datacentral.get(`/bot-tenders/document`, {
        responseType: 'blob',
        params: {
            file_id,
        },
    })
    return response
}

export const GetAllTenderDocuments = async (notify_id: string) => {
    const response = await datacentral.get(
        `/bot-tenders/document/download-all`,
        {
            responseType: 'blob',
            params: {
                notify_id,
            },
        }
    )
    return response
}

export const GetTenderBidResult = async (
    payload: DataGridPayload & { tender_id: number }
): Promise<ResponseArray<TenderBidResult>> => {
    const { tender_id, page, page_size, q, ...rest } = payload
    const response = await datacentral.get(
        `/bot-tenders/${tender_id}/bid-results`,
        {
            params: {
                page,
                pageSize: page_size,
                search: q,
                ...rest,
            },
        }
    )
    return response.data
}

export const listTenderBidResults = async (
    tenderId: number,
    params: Record<string, unknown>
) => {
    const response = await datacentral.get<ResponseArray<TenderBidResult>>(
        `/bot-tenders/${tenderId}/bid-results`,
        { params: remapTenderParams(params) }
    )
    return {
        results: response.data.results,
        pagination: response.data.pagination,
    }
}

export const UpdateTenderPotential = async (
    tender_id: number,
    is_potential: boolean,
    not_potential_reason: string | null
) => {
    if (is_potential) {
        const response = await datacentral.put(
            `/bot-tenders/${tender_id}/mark-potential`
        )
        return response.data
    } else {
        const response = await datacentral.put(
            `/bot-tenders/${tender_id}/unmark-potential`,
            {
                reason: not_potential_reason,
            }
        )
        return response.data
    }
}

export const ListClosingSoonTenders = async (
    employee_id: number
): Promise<ResponseArray<Tender>> => {
    const response = await datacentral.post(`/bot-tenders/closing-soon`, {
        employee_id,
    })
    return response.data
}

export const GetTenderByID = async (
    tender_id: number
): Promise<ResponseObject<Tender>> => {
    const response = await datacentral.get(`/bot-tenders/${tender_id}`)
    return response.data
}

export const UpdateTenderStatus = async (
    tender_id: number,
    is_followed: boolean
) => {
    const response = await datacentral.put(`/bot-tenders/${tender_id}/status`, {
        is_followed,
    })
    return response.data
}

export const UpdateTendersStatusBulk = async (
    tender_ids: number[],
    is_followed: boolean
) => {
    const response = await datacentral.put(`/bot-tenders/status/bulk`, {
        ids: tender_ids,
        is_followed: is_followed,
    })
    return response.data
}

export const ListNewTenders = async (
    payload: DataGridPayload
): Promise<ResponseArray<Tender>> => {
    const { page, page_size, q, ...rest } = payload
    const response = await datacentral.get('/bot-tenders/new', {
        params: {
            page,
            pageSize: page_size,
            search: q,
            ...rest,
        },
    })
    return response.data
}

// --- New standard-param service functions for DataTableCustomized ---

const remapTenderParams = (params: Record<string, unknown>) => {
    const { page, page_size, q, ...rest } = params
    return { page, pageSize: page_size, search: q, ...rest }
}

export const listNewTendersList = async (params: Record<string, unknown>) => {
    const response = await datacentral.get<ResponseArray<Tender>>(
        '/bot-tenders/new',
        { params: remapTenderParams(params) }
    )
    return {
        results: response.data.results,
        pagination: response.data.pagination,
    }
}

export const listFollowingTendersList = async (
    params: Record<string, unknown>
) => {
    const response = await datacentral.get<ResponseArray<Tender>>(
        '/bot-tenders/followed',
        { params: remapTenderParams(params) }
    )
    return {
        results: response.data.results,
        pagination: response.data.pagination,
    }
}

export const listUnfollowedTendersList = async (
    params: Record<string, unknown>
) => {
    const response = await datacentral.get<ResponseArray<Tender>>(
        '/bot-tenders/unfollowed',
        { params: remapTenderParams(params) }
    )
    return {
        results: response.data.results,
        pagination: response.data.pagination,
    }
}

export const GetTenderDocuments = async (params: {
    notify_id?: string
    url?: string
}): Promise<ResponseObject<TenderChapter[]>> => {
    const response = await datacentral.get('/bot-tenders/documents', {
        params,
    })
    return response.data
}

export const LookupTender = async (code: string) => {
    const response = await datacentral.get('/bot-tenders/lookup', {
        params: { code },
    })
    return response.data
}

export const AddTenderFromRemote = async (item: {
    notifyId: string
    code: string
    name: string
    bidCloseDate: string
    bidForm?: string
    field?: string
    location?: string
    investor?: string
    publishDate?: string
    detailUrl?: string
}): Promise<ResponseObject<{ id: number }>> => {
    const response = await datacentral.post<ResponseObject<{ id: number }>>(
        '/bot-tenders/from-remote',
        item
    )
    return response.data
}
